<template>
  <div class="warranty-section" aria-labelledby="warranty-heading">
    <header class="warranty-header">
      <div>
        <p class="eyebrow"><span></span> APPROVED REPAIR NETWORK</p>
        <h2 id="warranty-heading" class="text-4xl md:text-6xl font-bold text-white">Warranty <span class="text-primary">Service Centers</span></h2>
        <p class="intro text-xl text-white/80">The right repair. The right place. Find an approved location for your STE trailer, wherever the road takes you.</p>
      </div>
      <div class="network-summary">
        <strong>{{ centers.length }}</strong>
        <span>Service locations</span>
      </div>
    </header>

    <div class="locator">
      <div class="locator-toolbar">
        <form class="location-search" @submit.prevent="search">
          <label for="warranty-search">Find a center near you</label>
          <div class="search-controls">
            <div class="search-input-wrap">
              <font-awesome-icon :icon="['fas', 'magnifying-glass']" aria-hidden="true" />
              <input id="warranty-search" v-model="query" type="search" maxlength="100"
                placeholder="City, state, ZIP, or company" aria-describedby="warranty-search-help" />
            </div>
            <button class="search-button" type="submit" :disabled="Boolean(busy)">
              <font-awesome-icon v-if="busy" :icon="['fas', 'spinner']" spin aria-hidden="true" />
              {{ busy === 'search' ? 'Searching' : 'Search' }}
            </button>
            <button class="locate-button" type="button" :disabled="Boolean(busy)" @click="locate">
              <font-awesome-icon :icon="['fas', 'location-crosshairs']" aria-hidden="true" />
              {{ busy === 'locate' ? 'Locating…' : 'Use my location' }}
            </button>
          </div>
          <p id="warranty-search-help">Browse by city, state, or company. Enter a US ZIP code to sort by distance.</p>
        </form>
        <div class="filter-row">
          <div class="type-filters" role="group" aria-label="Certification filter">
            <button type="button" :aria-pressed="!certifiedOnly" @click="setCertification(false)">All centers</button>
            <button type="button" :aria-pressed="certifiedOnly" @click="setCertification(true)">
              <span class="mini-stamp" aria-hidden="true">R</span> R-stamp certified
            </button>
          </div>
          <label class="state-filter">
            <span class="sr-only">Filter centers by state</span>
            <select v-model="state" @change="filtersChanged">
              <option value="">All states</option>
              <option v-for="item in states" :key="item.code" :value="item.code">{{ item.name }}</option>
            </select>
          </label>
          <button v-if="hasFilters || busy || error" class="reset-button" type="button" @click="reset">Reset</button>
        </div>
        <p v-if="error" class="search-error" role="alert">{{ error }}</p>
      </div>

      <div class="locator-body">
        <div class="results-panel">
          <div class="results-heading" role="status" aria-live="polite" aria-atomic="true">
            <strong>{{ results.length }} {{ results.length === 1 ? 'center' : 'centers' }}{{ origin ? ' · nearest first' : '' }}</strong>
            <span v-if="origin">Near {{ origin.label }}</span>
            <span v-else-if="appliedQuery">Matching “{{ appliedQuery }}”</span>
            <span v-else>{{ state ? stateNames[state] : 'Explore the service network' }}</span>
          </div>
          <div ref="resultList" class="results-list" tabindex="0" aria-label="Warranty center results">
            <article v-for="center in results" :key="center.id" :data-center-id="center.id"
              class="center-card" :class="{ selected: selectedId === center.id }">
              <div class="center-card-top">
                <span class="provider-label" :class="{ 'named-provider': !center.shopNumber }">{{ center.provider }} <span v-if="center.shopNumber">/ {{ center.shopNumber }}</span></span>
                <span v-if="center.distance !== null" class="distance">{{ Math.round(center.distance).toLocaleString() }} mi</span>
              </div>
              <button type="button" class="center-name" :aria-pressed="selectedId === center.id"
                :aria-label="`Show ${labelFor(center)} on map`"
                @click="selectCenter(center.id)">
                {{ locationLabel(center) }}
                <font-awesome-icon :icon="['fas', 'location-dot']" aria-hidden="true" />
              </button>
              <p class="center-address" v-if="!center.approximateLocation">{{ center.address }}<br>{{ center.city }}, {{ center.state }} {{ center.postalCode }}</p>
              <p class="center-address" v-else>{{ center.city }}, {{ center.state }} {{ center.postalCode }}<br><span class="approximate-note">Approximate area · call for shop address</span></p>
              <span v-if="center.rStampCertified" class="certification-badge"><span aria-hidden="true">R</span> R-stamp certified</span>
              <div class="center-actions">
                <a v-if="center.phone" :href="`tel:+1${center.phone.replace(/\D/g, '')}`">
                  <font-awesome-icon :icon="['fas', 'phone']" aria-hidden="true" /> {{ center.phone }}
                </a>
                <a v-else-if="center.provider === 'QRS'" href="https://qrsshops.com/contact-us/" target="_blank" rel="noopener noreferrer">Contact QRS</a>
                <a :href="mapUrl(center)" target="_blank" rel="noopener noreferrer">
                  {{ center.approximateLocation ? 'View area' : 'Directions' }}
                  <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" aria-hidden="true" />
                </a>
              </div>
            </article>
            <div v-if="!results.length" class="empty-results">
              <font-awesome-icon :icon="['fas', 'map-location-dot']" aria-hidden="true" />
              <h3>No matching centers</h3>
              <p>Try another city or state, or enter a ZIP code to find the nearest options.</p>
              <button type="button" @click="reset">Show all centers</button>
            </div>
          </div>
          <p v-if="origin" class="distance-note">Approximate straight-line miles. Driving distances vary.</p>
        </div>

        <div class="map-panel">
          <div ref="mapElement" class="warranty-map" aria-label="Interactive map of warranty centers" @mousedown.capture="focusMap"></div>
          <div v-if="!mapReady" class="map-placeholder" role="status">
            <font-awesome-icon :icon="['fas', 'map-location-dot']" aria-hidden="true" />
            <p>{{ mapError ? 'The map is unavailable. All centers and directions are available in the list.' : 'Loading the repair network…' }}</p>
            <button v-if="mapError" type="button" @click="initializeMap">Retry map</button>
          </div>
          <div v-if="tileError && mapReady" class="tile-message" role="status">Map background unavailable. You can still select centers or use the list.</div>
          <button v-if="mapReady" class="fit-map-button" type="button" @click="fitMap(true)" aria-label="Show all matching centers on map">
            <font-awesome-icon :icon="['fas', 'expand']" aria-hidden="true" /> Show {{ hasFilters ? 'results' : 'all' }}
          </button>
          <div class="map-legend" aria-label="Map legend">
            <span><i class="legend-dot" aria-hidden="true"></i> Service center</span>
            <span><i class="legend-stamp" aria-hidden="true">R</i> R-stamp certified</span>
          </div>
        </div>
      </div>
      <div class="locator-footer">
        <p>QRS locations and R-stamp designations from <a href="https://qrsshops.com/locations/" target="_blank" rel="noopener noreferrer">QRS</a>.</p>
        <p>ZIP search by <a href="https://www.zippopotam.us/" target="_blank" rel="noopener noreferrer">Zippopotam.us</a>.</p>
      </div>
    </div>

  </div>
</template>

<script>
import { markRaw } from 'vue';
import centers from '../data/warrantyCenters.json';
import { STATE_NAMES, findCenters, lookupZip, centerMapUrl, centerLabel, centerLocationLabel } from '../utils/warrantyLocator.mjs';

export default {
  name: 'WarrantyCenters',
  data() {
    return {
      centers, stateNames: STATE_NAMES, query: '', appliedQuery: '', state: '',
      certifiedOnly: false, origin: null, selectedId: null, busy: '', error: '',
      mapReady: false, mapError: false, tileError: false
    };
  },
  computed: {
    states() {
      return [...new Set(this.centers.map(center => center.state))]
        .map(code => ({ code, name: STATE_NAMES[code] })).sort((a, b) => a.name.localeCompare(b.name));
    },
    results() {
      return findCenters(this.centers, { query: this.appliedQuery, state: this.state, certifiedOnly: this.certifiedOnly, origin: this.origin });
    },
    hasFilters() { return Boolean(this.query || this.appliedQuery || this.state || this.certifiedOnly || this.origin); }
  },
  mounted() {
    this.requestId = 0;
    this.destroyed = false;
    this.visibilityObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.visibilityObserver.disconnect();
        this.initializeMap();
      }
    });
    this.visibilityObserver.observe(this.$refs.mapElement);
  },
  beforeUnmount() {
    this.destroyed = true;
    this.cancelLookup();
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.map?.remove();
  },
  methods: {
    mapUrl: centerMapUrl,
    labelFor: centerLabel,
    locationLabel: centerLocationLabel,
    focusMap(event) {
      // Leaflet restores window scroll when focusing, but this page scrolls inside a div.
      // Focus without moving that container before Leaflet handles the pointer event.
      if (this.mapReady && event.button === 0 && !event.target.closest('a, button, input')) {
        this.$refs.mapElement.focus({ preventScroll: true });
      }
    },
    cancelLookup() {
      this.requestId += 1;
      this.lookupController?.abort();
      this.busy = '';
    },
    async search() {
      this.cancelLookup();
      this.error = '';
      const query = this.query.trim();
      const zip = query.match(/^(\d{5})(?:-\d{4})?$/);
      if (/^\d/.test(query) && !zip) {
        this.error = 'Enter a five-digit US ZIP code, or search by city or state.';
        return;
      }
      if (!zip) {
        this.origin = null;
        this.appliedQuery = query;
        this.refreshResults();
        return;
      }
      const requestId = this.requestId;
      this.busy = 'search';
      const controller = new AbortController();
      this.lookupController = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const origin = await lookupZip(zip[1], { signal: controller.signal });
        if (this.destroyed || requestId !== this.requestId) return;
        this.origin = origin;
        this.appliedQuery = '';
        this.refreshResults();
      } catch (error) {
        if (this.destroyed || requestId !== this.requestId) return;
        this.error = error.name === 'AbortError'
          ? 'ZIP search timed out. Try again, search by city or state, or use your location.'
          : error instanceof TypeError ? 'ZIP search is unavailable right now. Search by city or state, or use your location.' : error.message;
      } finally {
        clearTimeout(timeout);
        if (requestId === this.requestId) this.busy = '';
      }
    },
    locate() {
      this.cancelLookup();
      this.error = '';
      if (!navigator.geolocation) {
        this.error = 'Location is unavailable in this browser. Enter a ZIP code instead.';
        return;
      }
      const requestId = this.requestId;
      this.busy = 'locate';
      navigator.geolocation.getCurrentPosition(position => {
        if (this.destroyed || requestId !== this.requestId) return;
        this.origin = { latitude: position.coords.latitude, longitude: position.coords.longitude, label: 'your location' };
        this.query = '';
        this.appliedQuery = '';
        this.busy = '';
        this.refreshResults();
      }, error => {
        if (this.destroyed || requestId !== this.requestId) return;
        this.busy = '';
        this.error = error.code === 1
          ? 'Location access was declined. Enter a ZIP code to find nearby centers.'
          : 'We could not determine your location. Enter a ZIP code instead.';
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    },
    setCertification(value) { this.certifiedOnly = value; this.filtersChanged(); },
    filtersChanged() { this.cancelLookup(); this.error = ''; this.refreshResults(); },
    reset() {
      this.cancelLookup();
      this.query = ''; this.appliedQuery = ''; this.state = '';
      this.certifiedOnly = false; this.origin = null; this.error = '';
      this.refreshResults();
    },
    refreshResults() {
      this.selectedId = null;
      this.$refs.resultList.scrollTop = 0;
      this.$nextTick(() => {
        if (this.mapReady) { this.renderMarkers(); this.fitMap(); }
      });
    },
    async initializeMap() {
      if (this.mapReady || this.mapLoading || this.destroyed) return;
      this.mapLoading = true;
      this.mapError = false;
      try {
        const L = await import(/* webpackChunkName: "warranty-map" */ 'leaflet');
        await import(/* webpackChunkName: "warranty-map-style" */ 'leaflet/dist/leaflet.css');
        if (this.destroyed) return;
        this.leaflet = markRaw(L);
        this.map = markRaw(L.map(this.$refs.mapElement, {
          scrollWheelZoom: false, zoomControl: false, minZoom: 2, maxZoom: 18,
          dragging: !L.Browser.mobile, tapHold: false
        }));
        L.control.zoom({ position: 'bottomright' }).addTo(this.map);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
        }).on('tileerror', () => { this.tileError = true; })
          .on('tileload', () => { this.tileError = false; }).addTo(this.map);
        this.markerLayer = markRaw(L.layerGroup().addTo(this.map));
        this.mapReady = true;
        this.renderMarkers();
        this.fitMap(true);
        this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize({ pan: false }));
        this.resizeObserver.observe(this.$refs.mapElement);
      } catch {
        this.map?.remove();
        this.map = null;
        this.mapReady = false;
        this.mapError = true;
      } finally { this.mapLoading = false; }
    },
    renderMarkers() {
      const L = this.leaflet;
      this.markerLayer.clearLayers();
      this.markers = new Map();
      const groups = new Map();
      for (const center of this.results) {
        const key = `${center.latitude},${center.longitude}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(center);
      }
      for (const group of groups.values()) {
        const center = group[0];
        const certified = group.some(item => item.rStampCertified);
        const title = group.map(item => `${centerLabel(item)}${item.rStampCertified ? ' · R-stamp certified' : ''}`).join(' / ');
        const marker = L.marker([center.latitude, center.longitude], {
          title, alt: title,
          icon: L.divIcon({
            className: `warranty-pin ${certified ? 'certified-pin' : ''}`,
            html: `<span>${group.length > 1 ? group.length : certified ? 'R' : '<i></i>'}</span>`,
            iconSize: [28, 28], iconAnchor: [14, 14]
          })
        }).addTo(this.markerLayer);
        const popup = document.createElement('div');
        popup.className = 'warranty-popup';
        for (const item of group) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = centerLabel(item);
          button.addEventListener('click', () => this.selectCenter(item.id));
          popup.appendChild(button);
          const detail = document.createElement('p');
          detail.textContent = item.approximateLocation ? 'Approximate area · call for shop address' : item.address;
          popup.appendChild(detail);
          const badge = document.createElement('p');
          badge.textContent = item.rStampCertified ? 'R-stamp certified' : 'Service center';
          popup.appendChild(badge);
          const directions = document.createElement('a');
          directions.href = centerMapUrl(item); directions.target = '_blank'; directions.rel = 'noopener noreferrer';
          directions.textContent = item.approximateLocation ? 'View area ↗' : 'Get directions ↗';
          popup.appendChild(directions);
        }
        marker.bindPopup(popup, { maxWidth: 280 });
        marker.on('click', () => this.highlightCenter(center.id));
        group.forEach(item => this.markers.set(item.id, marker));
      }
      if (this.origin) {
        L.circleMarker([this.origin.latitude, this.origin.longitude], { radius: 7, color: '#fff', weight: 3, fillColor: 'var(--color-info)', fillOpacity: 1 })
          .bindTooltip(document.createTextNode(this.origin.label)).addTo(this.markerLayer);
      }
    },
    fitMap(all = false) {
      if (!this.mapReady) return;
      const shown = this.origin && !all ? this.results.slice(0, 5) : this.results;
      const points = shown.map(center => [center.latitude, center.longitude]);
      if (this.origin) points.push([this.origin.latitude, this.origin.longitude]);
      if (points.length) this.map.fitBounds(points, { padding: [42, 42], maxZoom: 10, animate: false });
      else this.map.setView([38.8, -97], 4);
    },
    highlightCenter(id) {
      this.selectedId = id;
      this.$nextTick(() => {
        const list = this.$refs.resultList;
        const card = list.querySelector(`[data-center-id="${id}"]`);
        if (card) list.scrollTop += card.getBoundingClientRect().top - list.getBoundingClientRect().top;
      });
    },
    selectCenter(id) {
      this.highlightCenter(id);
      if (!this.mapReady) return;
      const marker = this.markers.get(id);
      if (marker) {
        this.map.setView(marker.getLatLng(), 11, { animate: false });
        marker.openPopup();
      }
    }
  }
};
</script>

<style scoped>
.warranty-section { padding: 100px 0 60px; color: #fff; text-align: left; font-family: inherit; font-size: .9rem; }
.warranty-section button, .warranty-section input, .warranty-section select { font-family: inherit; }
.intro { max-width: 48rem; line-height: 1.6; }
.warranty-header { display: flex; align-items: center; justify-content: space-between; gap: 40px; margin-bottom: 38px; }
.eyebrow { display: flex; align-items: center; gap: 12px; font-size: .8rem; font-weight: 700; letter-spacing: 2.2px; color: var(--color-secondary); margin-bottom: 18px; }
.eyebrow span { width: 30px; height: 2px; background: var(--color-primary); }
.warranty-header h2 { margin: 0 0 1.5rem; }
.network-summary { display: flex; align-items: center; gap: 18px; flex-shrink: 0; padding-left: 28px; border-left: 1px solid #ffffff20; }
.network-summary strong { font-size: 3rem; line-height: 1; font-weight: 700; color: #fff; }
.network-summary span { color: var(--color-secondary); font-size: .9rem; line-height: 1.65; }
.locator { border: 1px solid #ffffff1c; border-radius: 16px; background: var(--color-base-200); overflow: hidden; box-shadow: 0 20px 60px #00000026; }
.locator-toolbar { padding: 25px 28px 20px; }
.location-search > label { display: block; font-size: .9rem; font-weight: 500; margin-bottom: 10px; }
.search-controls { display: flex; gap: 10px; }
.search-input-wrap { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; border: 1px solid #ffffff29; border-radius: 7px; padding: 0 14px; background: var(--color-base-300); color: var(--color-secondary); }
.search-input-wrap:focus-within { border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-primary) 20%, transparent); }
.search-input-wrap input { min-width: 0; width: 100%; background: transparent; color: #fff; height: 46px; font: inherit; font-size: .9rem; outline: none; }
.search-input-wrap input::placeholder { color: var(--color-secondary); opacity: 1; }
.search-input-wrap input::-webkit-search-cancel-button { cursor: pointer; }
.search-button, .locate-button { min-height: 46px; padding: 0 23px; border-radius: 7px; font-size: .9rem; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 9px; cursor: pointer; }
.search-button { background: var(--color-primary); color: #fff; border: 1px solid var(--color-primary); }
.search-button:hover { background: color-mix(in srgb, var(--color-primary) 90%, transparent); }
.locate-button { border: 1px solid #ffffff29; color: #fff; background: transparent; }
.locate-button:hover { background: #ffffff09; }
button:disabled { cursor: wait; opacity: .6; }
.location-search > p { font-size: .8rem; color: rgba(255, 255, 255, 0.7); margin-top: 9px; }
.filter-row { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-top: 21px; }
.type-filters { display: inline-flex; background: var(--color-base-300); border: 1px solid #ffffff17; border-radius: 7px; padding: 3px; }
.type-filters button { display: inline-flex; align-items: center; gap: 7px; font-size: .8rem; color: var(--color-secondary); padding: 7px 13px; border-radius: 4px; cursor: pointer; }
.type-filters button[aria-pressed="true"] { background: var(--color-base-100); color: #fff; box-shadow: 0 1px 4px #0003; }
.mini-stamp { color: #fff; font-size: .65rem; border: 1px solid var(--color-primary); background: var(--color-primary); border-radius: 3px; width: 1rem; height: 1rem; display: inline-flex; align-items: center; justify-content: center; }
.state-filter select { background: var(--color-base-300); border: 1px solid #ffffff29; color: rgba(255, 255, 255, 0.9); border-radius: 6px; padding: 9px 28px 9px 12px; font-family: inherit; font-size: .8rem; cursor: pointer; max-width: 210px; }
.reset-button { margin-left: auto; padding: 8px; color: var(--color-secondary); font-size: .8rem; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.search-error { margin-top: 14px; padding: 10px 14px; color: #fff; background: color-mix(in srgb, var(--color-primary) 15%, transparent); border-left: 2px solid var(--color-primary); border-radius: 3px; font-size: .9rem; }
.locator-body { display: grid; grid-template-columns: 350px minmax(0, 1fr); border-top: 1px solid #ffffff17; }
.results-panel { display: flex; flex-direction: column; min-height: 0; height: 570px; background: var(--color-base-300); }
.results-heading { min-height: 76px; padding: 17px 22px; display: flex; flex-direction: column; gap: 4px; border-bottom: 1px solid #ffffff14; }
.results-heading strong { font-size: .9rem; font-weight: 700; }
.results-heading > span { color: rgba(255, 255, 255, 0.7); font-size: .8rem; }
.results-list { overflow-y: auto; flex: 1; min-height: 0; scrollbar-width: thin; scrollbar-color: var(--color-secondary) var(--color-base-300); overscroll-behavior: contain; }
.center-card { padding: 20px 22px; border-bottom: 1px solid #ffffff14; border-left: 3px solid transparent; transition: background .15s; }
.center-card:hover { background: #ffffff04; }
.center-card.selected { background: color-mix(in srgb, var(--color-primary) 8%, transparent); border-left-color: var(--color-primary); }
.center-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 7px; }
.provider-label { font-size: .75rem; letter-spacing: 1.2px; font-weight: 700; color: rgba(255, 255, 255, 0.9); min-width: 0; overflow-wrap: anywhere; }
.provider-label.named-provider { font-size: .9rem; letter-spacing: normal; }
.provider-label > span { color: var(--color-secondary); font-weight: 400; }
.distance { font-size: .8rem; color: var(--color-secondary); flex-shrink: 0; }
.center-name { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; text-align: left; font-size: 1.25rem; font-weight: 700; line-height: 1.25; color: #fff; cursor: pointer; }
.center-name svg { color: var(--color-secondary); font-size: .8rem; flex-shrink: 0; }
.center-name:hover { color: var(--color-secondary); }
.center-address { font-style: normal; color: rgba(255, 255, 255, 0.8); font-size: .9rem; line-height: 1.65; margin-top: 10px; }
.approximate-note { color: var(--color-secondary); font-size: .8rem; }
.certification-badge { display: inline-flex; align-items: center; gap: 6px; margin-top: 10px; font-size: .75rem; color: #fff; background: color-mix(in srgb, var(--color-primary) 12%, transparent); padding: 3px 7px; border: 1px solid color-mix(in srgb, var(--color-primary) 40%, transparent); border-radius: 4px; }
.certification-badge > span { font-size: .65rem; font-weight: 700; }
.center-actions { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-top: 15px; }
.center-actions a { font-size: .8rem; color: rgba(255, 255, 255, 0.9); text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.center-actions a:last-child { color: var(--color-secondary); }
.center-actions a:hover { text-decoration: underline; text-underline-offset: 3px; }
.center-actions svg { font-size: .75rem; }
.distance-note { padding: 10px 18px; color: rgba(255, 255, 255, 0.7); border-top: 1px solid #ffffff14; font-size: .75rem; }
.empty-results { padding: 40px 24px; text-align: center; }
.empty-results > svg { color: var(--color-secondary); font-size: 30px; margin-bottom: 15px; }
.empty-results h3 { font-size: 1.25rem; font-weight: 700; }
.empty-results p { font-size: .9rem; color: rgba(255, 255, 255, 0.8); margin: 10px 0 20px; line-height: 1.5; }
.empty-results button, .map-placeholder button { font-size: .9rem; color: var(--color-secondary); text-decoration: underline; cursor: pointer; }
.map-panel { position: relative; min-width: 0; background: #e5e7e7; isolation: isolate; }
.warranty-map { height: 570px; width: 100%; z-index: 0; background: #e5e7e7; }
.map-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 40px; gap: 15px; background: var(--color-base-200); color: var(--color-secondary); text-align: center; font-size: .9rem; }
.map-placeholder > svg { font-size: 32px; }
.tile-message { position: absolute; top: 65px; left: 15px; right: 15px; padding: 9px 12px; border-radius: 5px; background: var(--color-base-200); font-size: .8rem; }
.fit-map-button { position: absolute; top: 16px; right: 16px; display: flex; align-items: center; gap: 8px; color: var(--color-base-300); background: #fffffff2; border: 1px solid var(--color-secondary); border-radius: 6px; box-shadow: 0 2px 9px #0001; padding: 10px 13px; font-size: .8rem; cursor: pointer; }
.map-legend { position: absolute; top: 16px; left: 16px; display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 10px 13px; border: 1px solid var(--color-secondary); border-radius: 6px; background: #fffffff2; color: var(--color-base-300); box-shadow: 0 2px 9px #0001; font-size: .8rem; }
.map-legend > span { display: inline-flex; align-items: center; gap: 6px; }
.legend-dot { width: 11px; height: 11px; background: var(--color-base-100); border-radius: 50%; display: inline-block; border: 2px solid #fff; box-shadow: 0 0 0 1px var(--color-base-100); }
.legend-stamp { display: inline-flex; align-items: center; justify-content: center; font-style: normal; font-size: .65rem; font-weight: 700; width: 1rem; height: 1rem; background: var(--color-primary); border-radius: 50%; color: #fff; }
.locator-footer { display: flex; justify-content: space-between; gap: 15px; flex-wrap: wrap; padding: 12px 22px; border-top: 1px solid #ffffff14; color: rgba(255, 255, 255, 0.7); font-size: .75rem; }
.locator-footer a { text-decoration: underline; text-underline-offset: 2px; color: var(--color-secondary); }
button:focus-visible, a:focus-visible, select:focus-visible, .results-list:focus-visible { outline: 2px solid var(--color-secondary); outline-offset: 3px; }
:deep(.leaflet-tile-pane) { filter: saturate(.25); }
:deep(.leaflet-container), :deep(.leaflet-control), :deep(.leaflet-popup), :deep(.leaflet-tooltip) { font-family: inherit; }
:deep(.leaflet-control-zoom a) { font-family: inherit; }
:deep(.leaflet-control-attribution) { font-size: .75rem; background: #fffffff2; color: #333; }
:deep(.leaflet-control-attribution a) { color: var(--color-primary); }
:deep(.warranty-pin) { background: transparent; border: 0; }
:deep(.warranty-pin > span) { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; border-radius: 50%; background: var(--color-base-100); color: #fff; font-family: inherit; font-size: .75rem; font-weight: 700; box-shadow: 0 2px 7px #0005; }
:deep(.warranty-pin > span > i) { width: 6px; height: 6px; border-radius: 50%; background: #fff; }
:deep(.certified-pin > span) { background: var(--color-primary); }
:deep(.warranty-pin:focus-visible) { outline: 3px solid var(--color-primary); outline-offset: 4px; border-radius: 50%; }
:deep(.warranty-popup) { font-size: .9rem; color: var(--color-base-100); line-height: 1.5; }
:deep(.warranty-popup button) { display: block; text-align: left; color: var(--color-base-200); font-weight: 700; font-size: .9rem; cursor: pointer; margin-top: 10px; }
:deep(.warranty-popup p) { margin: 5px 0; }
:deep(.warranty-popup a) { color: var(--color-primary); font-weight: 700; }
@media (max-width: 1100px) {
  .locator-body { grid-template-columns: 315px minmax(0, 1fr); }
  .network-summary { display: none; }
  .map-legend { flex-direction: column; align-items: flex-start; gap: 7px; }
}
@media (max-width: 760px) {
  .warranty-section { padding: 100px 0 50px; }
  .warranty-header { margin-bottom: 25px; }
  .locator-toolbar { padding: 20px 17px; }
  .search-controls { flex-wrap: wrap; }
  .search-input-wrap { flex-basis: calc(100% - 106px); }
  .search-button { padding: 0 17px; }
  .locate-button { min-height: 38px; padding: 0; border: 0; color: var(--color-secondary); font-size: .9rem; }
  .filter-row { gap: 10px; margin-top: 15px; }
  .type-filters button { padding: 7px 9px; font-size: .8rem; }
  .state-filter select { max-width: 160px; }
  .locator-body { display: flex; flex-direction: column; }
  .map-panel { order: -1; }
  .warranty-map { height: 360px; }
  .results-panel { height: 470px; }
  .map-legend { font-size: .75rem; padding: 8px 10px; top: 12px; left: 12px; }
  .fit-map-button { font-size: .8rem; padding: 9px 10px; top: 12px; right: 12px; }
  .results-heading { min-height: 70px; }
  .center-card { padding: 18px 20px; }
  .center-actions a { font-size: .9rem; }
  .locator-footer { gap: 6px; padding: 12px 17px; }
}
@media (max-width: 480px) {
  .search-input-wrap { flex-basis: 100%; }
}
@media (prefers-reduced-motion: reduce) { .center-card { transition: none; } }
</style>
